import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UsersIcon,
  StarIcon,
  PlusIcon,
  CogIcon,
  SparklesIcon,
  CubeIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const CompanyDashboard = () => {
  const {t} = useTranslation();
  const { user, apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    metrics: { totalRevenue: 0, totalBookings: 0, newCustomers: 0, averageRating: 0 },
    recentBookings: [],
    topServices: [],
    notifications: []
  });

  useEffect(() => {
    const companyId = user?.company_id || user?.companyId;
    if (companyId) loadDashboardData(companyId);
  }, [user]);

  const loadDashboardData = async (companyId) => {
    try {
      setLoading(true);
      const [metricsRes, bookingsRes, servicesRes] = await Promise.allSettled([
        apiCall(`/companies/analytics/dashboard?days=30`),
        apiCall(`/companies/bookings?limit=5`),
        apiCall(`/companies/services?limit=5`)
      ]);

      const m = metricsRes.status === 'fulfilled' ? metricsRes.value?.data : {};
      const b = bookingsRes.status === 'fulfilled' ? (bookingsRes.value?.bookings || bookingsRes.value?.data) : [];
      const s = servicesRes.status === 'fulfilled' ? (servicesRes.value?.services || servicesRes.value?.data) : [];

      setDashboardData({
        metrics: {
          totalRevenue: m?.total_revenue || 0,
          totalBookings: m?.total_bookings || 0,
          newCustomers: m?.total_customers || 0,
          averageRating: m?.average_rating || 0
        },
        recentBookings: Array.isArray(b) ? b : [],
        topServices: Array.isArray(s) ? s : [],
        notifications: []
      });
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-10 bg-gray-100 rounded-xl w-48"></div>
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-gray-50 rounded-3xl"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* HEADER */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-[#0A1128] uppercase italic tracking-tighter">
            {t("company.dashboard.title_lable")} <span className="text-slate-300">{t("company.dashboard.title_lable_span")}</span>
          </h1>
        </div>
        <Link to="/company/analytics" className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-[#0A1128] transition-colors">
          {t("company.dashboard.btn_detail")} <ArrowRightIcon className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* METRICS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: t("company.dashboard.metrics.revenue"), value: formatCurrency(dashboardData.metrics.totalRevenue), icon: CurrencyDollarIcon, color: 'text-blue-500', bg: 'bg-blue-50' },
          { label: t("company.dashboard.metrics.bookings"), value: dashboardData.metrics.totalBookings, icon: CalendarDaysIcon, color: 'text-orange-500', bg: 'bg-orange-50' },
          { label: t("company.dashboard.metrics.customers"), value: dashboardData.metrics.newCustomers, icon: UsersIcon, color: 'text-purple-500', bg: 'bg-purple-50' },
          { label: t("company.dashboard.metrics.rating"), value: `${dashboardData.metrics.averageRating}/5.0`, icon: StarIcon, color: 'text-yellow-500', bg: 'bg-yellow-50' },
        ].map((m, i) => (
          <div key={i} className="bg-white p-8 rounded-[32px] border border-gray-50 shadow-sm hover:shadow-md transition-all duration-300 group">
            <div className={`w-12 h-12 ${m.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
              <m.icon className={`h-6 w-6 ${m.color}`} />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{m.label}</p>
            <p className="text-2xl font-black text-[#0A1128] italic tracking-tight">{m.value}</p>
          </div>
        ))}
      </div>

      {/* QUICK ACTIONS */}
      <section>
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-900 mb-6 flex items-center gap-3">
          {t("company.dashboard.quick_actions.lable")} <div className="h-px w-10 bg-slate-100"></div>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { name: t("company.dashboard.quick_actions.service") , icon: PlusIcon, href: '/company/services' },
            { name: t("company.dashboard.quick_actions.calendar") , icon: CalendarDaysIcon, href: '/company/calendar' },
            { name: t("company.dashboard.quick_actions.customers") , icon: UsersIcon, href: '/company/customers' },
            { name: t("company.dashboard.quick_actions.settings") , icon: CogIcon, href: '/company/settings' },
            { name: t("company.dashboard.quick_actions.ai_prompts") , icon: SparklesIcon, href: '/company/ai-prompts' },
            { name: t("company.dashboard.quick_actions.inventory") , icon: CubeIcon, href: '/company/inventory' },
          ].map((action, i) => (
            <Link key={i} to={action.href} className="flex flex-col items-center justify-center p-6 bg-white border border-gray-50 rounded-[24px] hover:bg-slate-900 hover:text-white transition-all duration-300 group shadow-sm">
              <action.icon className="h-6 w-6 mb-3 text-slate-400 group-hover:text-[#FF5C35] transition-colors" />
              <span className="text-[9px] font-black uppercase tracking-tighter text-center">{action.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* TABLES ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* RECENT BOOKINGS */}
        <div className="bg-white rounded-[40px] border border-gray-50 p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-[12px] font-black uppercase tracking-widest text-[#0A1128] italic">{t("company.dashboard.recent_bookings.title")}</h3>
            <Link to="/company/bookings" className="text-[9px] font-black uppercase text-[#FF5C35] hover:underline">{t("company.dashboard.recent_bookings.view_all")}</Link>
          </div>
          <div className="space-y-4">
            {dashboardData.recentBookings.length > 0 ? dashboardData.recentBookings.map((b, i) => (
              <div key={i} className="flex items-center justify-between p-5 bg-[#F8F9FB] rounded-[24px] border border-gray-50/50 hover:border-orange-100 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-lg italic font-black text-slate-200">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-[11px] font-black text-[#0A1128] uppercase">{b.service_name || 'Service'}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">{b.customer_name || 'Client'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-black text-[#0A1128] italic">{formatCurrency(b.total_amount)}</p>
                  <p className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-md mt-1 inline-block ${
                    b.status === 'completed' ? 'bg-green-50 text-green-500' : 'bg-orange-50 text-[#FF5C35]'
                  }`}>{b.status || 'Pending'}</p>
                </div>
              </div>
            )) : <p className="text-center py-10 text-[10px] font-bold uppercase text-slate-300 tracking-widest">{t("company.dashboard.recent_bookings.empty")}</p>}
          </div>
        </div>

        {/* POPULAR SERVICES */}
        <div className="bg-white rounded-[40px] border border-gray-50 p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-[12px] font-black uppercase tracking-widest text-[#0A1128] italic">{t("company.dashboard.top_services.title")}</h3>
            <Link to="/company/services" className="text-[9px] font-black uppercase text-slate-400 hover:text-[#0A1128]">{t("company.dashboard.top_services.edit")}</Link>
          </div>
          <div className="space-y-6">
            {dashboardData.topServices.length > 0 ? dashboardData.topServices.map((s, i) => (
              <div key={i} className="flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#FF5C35]"></div>
                  <div>
                    <p className="text-[11px] font-black text-[#0A1128] uppercase tracking-tight">{s.name}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase">{s.bookings_count || 0} sales</p>
                  </div>
                </div>
                <p className="text-[11px] font-black text-slate-900 italic">{formatCurrency(s.price)}</p>
              </div>
            )) : <p className="text-center py-10 text-[10px] font-bold uppercase text-slate-300 tracking-widest">{t("company.dashboard.top_services.empty")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;